import { Component, EventEmitter, inject, OnInit, Output, signal } from '@angular/core';

import { FilterBarComponent } from '../../../../shared/components/filter-bar/filter-bar.component';
import { FILTERS_CLIENT } from '../../../../core/tokens/filters-client.token';
import { LocalStorageService } from '../../../../core/services/local-storage.service';
import { LocalStorageKeys } from '../../../../shared/constants/local-storage-keys';
import { MATRIX_FILTER_KEYS } from '../../../../shared/constants/matrix-filter-key.constants';
import type { IFilterConfig, IFilterResponse, INode } from '../../../../shared/components/filter-bar/models/filter.model';

@Component({
  selector: 'app-dashboard-filter-bar',
  templateUrl: './dashboard-filter-bar.component.html',
  standalone: true,
  imports: [FilterBarComponent],
})
export class DashboardFilterBarComponent implements OnInit {
  @Output() applyFilters = new EventEmitter<Record<string, unknown>>();
  @Output() infoIconHoverChange = new EventEmitter<boolean>();

  private readonly filtersClient = inject(FILTERS_CLIENT);
  private readonly localStorageService = inject(LocalStorageService);

  /**
   * Maps each location parent value to the full set of values to send when it is selected:
   * the parent itself plus all its direct children.
   */
  private locationExpansionMap = new Map<string, string[]>();

  readonly filtersConfig = signal<IFilterConfig[]>([]);

  ngOnInit(): void {
    const organizationId =
      this.localStorageService.getItem(LocalStorageKeys.OrganizationId) ?? '';

    this.filtersClient.getFilters(organizationId).subscribe((response) => {
      this.locationExpansionMap = this.buildLocationExpansionMap(response.location);
      this.filtersConfig.set(this.mapResponseToConfig(response));
    });
  }

  onApplyFilters(value: Record<string, unknown>): void {
    const locationKey = MATRIX_FILTER_KEYS.LOCATION;
    const locationValues = value[locationKey];

    if (Array.isArray(locationValues) && locationValues.length > 0) {
      const expanded = (locationValues as (INode | string)[]).flatMap((v) => {
        // The filter bar emits raw form values: INode objects for tree-selects.
        const guid = typeof v === 'string' ? v : v.value;
        return this.locationExpansionMap.get(guid) ?? [guid];
      });
      this.applyFilters.emit({ ...value, [locationKey]: expanded });
    } else {
      this.applyFilters.emit(value);
    }
  }

  private buildLocationExpansionMap(locations: INode[]): Map<string, string[]> {
    const map = new Map<string, string[]>();
    for (const loc of locations) {
      const childValues = loc.children?.map((c) => c.value) ?? [];
      map.set(loc.value, [loc.value, ...childValues]);
    }
    return map;
  }

  private mapResponseToConfig(response: IFilterResponse): IFilterConfig[] {
    return [
      {
        id: MATRIX_FILTER_KEYS.MEDICATION,
        label: 'Medication',
        value: MATRIX_FILTER_KEYS.MEDICATION,
        type: 'tree-select',
        showNodeToggle: false,
        nodes: response.medication.map(this.toNode),
      },
      {
        id: MATRIX_FILTER_KEYS.LOCATION,
        label: 'Location',
        value: MATRIX_FILTER_KEYS.LOCATION,
        type: 'tree-select',
        showNodeToggle: false,
        // Children are intentionally stripped: only parent locations are shown in the UI.
        // When a parent is selected, its children values are expanded in onApplyFilters.
        nodes: response.location.map(({ key, label, value }) => ({ key, label, value }) satisfies INode),
      },
      {
        id: MATRIX_FILTER_KEYS.SERVICE_DEPARTMENT,
        label: 'Service Dept',
        value: MATRIX_FILTER_KEYS.SERVICE_DEPARTMENT,
        type: 'tree-select',
        nodes: response.serviceDepartment.map(this.toNode),
      },
    ];
  }

  private toNode(dto: INode): INode {
    return { key: dto.key, label: dto.label, value: dto.value };
  }
}
